// What the masterminds' staff side reads and writes (docs/data-model.md 10.6.1 and 10.6.9), as the staff
// member, with their own session at 2-step login. The reads: hub.mastermind_standing for the person page's
// lines, hub.mastermind_members for the Masterminds section's members and the person page's card,
// hub.accountability_log for the member view and the log (1 read that holds the goal and its versions,
// every session judged, every month with its actions and how each went, the wins and the copies kept),
// hub.mastermind_history for the history of changes, hub.accountability_copy for a copy kept, the
// product's sessions for the session a month is set at, and hub.accountability_rules for Guarantee rules'
// numbers (migration 81). The writes go through the staff definers (lock_goal, unlock_goal,
// mark_goal_reached, issue_actions, close_month, edit_action, set_aside_action, put_back_action,
// correct_outcome, record_catch_up, set_accountability_rules, note_log_saved, and migration 80's
// set_accountability_level and set_month_session), which ask the database who is acting themselves: owner or admin,
// at 2-step login, never while viewing as. Wins
// are direct writes under row level security, as are register marks. Groups, a session for every group
// and Mark all present are the direct writes Recordings and dates already makes.
//
// Each refusal comes back as an AdminError whose message is the line to show (words.js refusalWords,
// from the code the definer raises as its message).
import { ENV } from '../../lib/env.js'
import { MM_COLUMNS } from './columns.js'
import { ACCESS_COLUMNS, BILLING_COLUMNS, PEOPLE_BILLING_COLUMNS } from '../people/columns.js'
import { refusalWords, GROUPS } from './words.js'

const args = (a = {}) => Object.fromEntries(Object.entries(a).filter(([, v]) => v !== undefined).map(([k, v]) => [k.startsWith('p_') ? k : `p_${k}`, v]))

// A refusal with the mastermind's own words, else the admin api's
function refused(api, error, ctx) {
  const words = refusalWords(error, ctx)
  const base = api.AdminError && error instanceof api.AdminError ? error : null
  if (words) return new api.AdminError(error?.code || 'refused', base?.status || 400, words)
  return base || new api.AdminError(error?.code || 'failed', 0, api.words(error))
}

// A staff definer or read by its database name, its answer or a refusal in words
async function call(api, name, a, ctx) {
  let res
  try {
    res = await api.db.rpc(name, args(a))
  } catch (e) {
    throw refused(api, e, ctx)
  }
  if (res.error) throw refused(api, res.error, ctx)
  return res.data
}

// A direct write's rows, or a refusal in words
async function write(api, query, ctx, wordsFor) {
  let res
  try {
    res = await query
  } catch (e) {
    throw refused(api, e, ctx)
  }
  if (res.error) {
    const own = wordsFor?.(res.error)
    if (own) throw new api.AdminError(res.error.code || 'refused', 400, own)
    throw refused(api, res.error, ctx)
  }
  return res.data
}

// ---- Reads --------------------------------------------------------------------------------------------

// Whether the person page asks for the standing: an access row on a mastermind in the hub
export const hasMastermind = data => (data?.access || []).some(a => {
  const p = (data.catalogue || []).find(x => x.id === a.product_id)
  return p?.type === 'mastermind' && p.moved_in
})

// Each mastermind the person has an access row on, with their group, term month, attendance and the
// guarantee, goal, this month's actions and wins (hub.mastermind_standing). Nothing for a role that
// can't read them (a host of another product), so the page leaves the Accelerator's lines as they were.
export async function loadStanding(api, personId) {
  try {
    return (await call(api, 'mastermind_standing', { person: personId })) || []
  } catch {
    return []
  }
}

// ---- The Masterminds section and the accountability log (owner, admin) ---------------------------------

// The masterminds that have moved into the hub, from the catalogue as admin lists it
export async function loadMasterminds(api) {
  return (await api.products()).filter(p => p.type === 'mastermind' && p.moved_in)
}
// A mastermind's members now (or, with left, the ones who've ended or been revoked), each with their
// accountability log's summary; with person, just that 1 person, whatever their status
export const loadMembers = (api, productId, { left = false, person } = {}) => call(api, 'mastermind_members', { product: productId, person, left: left || undefined })
// 1 member's accountability log, the staff version (owner, admin; null for anyone else)
export const loadLog = (api, personId, productId) => call(api, 'accountability_log', { person: personId, product: productId })
// The history of changes: every change to their goal and actions, and the audit lines beside them
export const loadHistory = (api, personId, productId) => call(api, 'mastermind_history', { person: personId, product: productId })
// A copy of the log kept when it was sent or saved
export const loadCopy = (api, eventId) => call(api, 'accountability_copy', { event: eventId })
// A mastermind's sessions, for the session a month is set at, and its groups and their WhatsApp links
export const loadSessions = (api, productId) => api.run(api.from('sessions').select(MM_COLUMNS.sessions).eq('product_id', productId)
  .order('starts_at', { nullsFirst: false }).order('id'))
export const loadGroups = (api, productId) => api.run(api.from('cohorts').select(MM_COLUMNS.groups).eq('product_id', productId)
  .order('sort_order').order('id'))
export const loadLinks = api => api.run(api.from('cohort_links').select(MM_COLUMNS.links).order('cohort_id')).catch(() => [])
// The after-session checklist's reads (the critique's admin 7), each failure-tolerant: null leaves its line out.
// The sessions still to come, with their Zoom links (as Adam typed them: the hub never reads Zoom), and the recordings
// with the session each is of.
export const loadNextSessions = (api, productId, nowMs = Date.now()) => api.run(api.from('sessions').select(MM_COLUMNS.nextSessions)
  .eq('product_id', productId).gt('starts_at', new Date(nowMs).toISOString()).order('starts_at').order('id')).catch(() => null)
export const loadRecordings = (api, productId) => api.run(api.from('recordings').select(MM_COLUMNS.recordings)
  .eq('product_id', productId).not('session_id', 'is', null).order('id')).catch(() => null)
// Guarantee rules' numbers: since migration 81 (decision 56) in hub.accountability_rules, out of the product's
// settings, which every member who sees the product reads. The mastermind's rules ({} when it has none, so
// private.mm_rules's defaults apply), or null on a database before 81, whose Data API has no such table (404,
// PGRST205, as staging answers a table it hasn't got; 42P01 from Postgres itself): the numbers are still in the
// product's settings there (rulesFrom). Failure-tolerant like the checklist's reads: any other refusal reads the
// settings too, so the members list never waits on the card.
export const loadRules = (api, productId) => api.run(api.from('accountability_rules').select(MM_COLUMNS.rules).eq('product_id', productId))
  .then(rows => rows?.[0]?.rules || {}).catch(() => null)

// The member's access rows and their billing, as the person page reads them (src/admin/people/data.js), for the
// status line and its buttons (the critique's admin 5): the writes are the person page's own. Failure-tolerant: with
// nothing read, there's no status line.
export async function loadAccessFor(api, personId) {
  try {
    const access = await api.run(api.from('access').select(ACCESS_COLUMNS).eq('person_id', personId).order('starts_at', { ascending: false }).order('id'))
    const money = access.filter(a => a.status === 'overdue' || a.status === 'suspended')
    const [billing, people] = money.length
      ? await Promise.all([
        api.run(api.from('access_billing').select(BILLING_COLUMNS).in('access_id', access.map(a => a.id).sort())).catch(() => []),
        api.run(api.from('people_billing').select(PEOPLE_BILLING_COLUMNS).eq('person_id', personId)).catch(() => []),
      ])
      : [[], []]
    return { access, billing, hasCustomer: Boolean(people[0]?.stripe_customer_id) }
  } catch {
    return null
  }
}

// Each mastermind's sessions, for History's register lines: a group session reads "Present at Group 2,
// session 2", its number in its group, as its recording is titled (person_history gives its id only)
export async function loadGroupSessions(api, productIds) {
  const ids = [...new Set(productIds)].sort()
  const lists = await Promise.all(ids.map(id => api.run(api.from('sessions').select(MM_COLUMNS.sessions)
    .eq('product_id', id).order('starts_at', { nullsFirst: false }).order('id')).catch(() => [])))
  return lists.flat()
}

// ---- The staff definers (owner, admin) ----------------------------------------------------------------

// the goal: the day it was agreed goes with the first lock (never before they joined or in the future)
export const lockGoal = (api, { person, product, body, agreedOn }, ctx) => call(api, 'lock_goal', { person, product, body: body?.trim() ? body.trim() : undefined, agreed_on: agreedOn || undefined }, ctx)
export const unlockGoal = (api, { person, product }, ctx) => call(api, 'unlock_goal', { person, product }, ctx)
export const markReached = (api, { person, product, on }, ctx) => call(api, 'mark_goal_reached', { person, product, reached: on || null }, { ...ctx, reach: true })
// this month's actions
export const issueActions = (api, { person, product, bodies, session }, ctx) => call(api, 'issue_actions', { person, product, bodies, session: session || null }, ctx)
export const editAction = (api, { action, body }, ctx) => call(api, 'edit_action', { action, body }, ctx)
export const setAside = (api, { action, reason }, ctx) => call(api, 'set_aside_action', { action, reason }, { ...ctx, aside: true })
export const putBack = (api, { action }, ctx) => call(api, 'put_back_action', { action }, ctx)
// the review: how each action went, what carries, the new actions and the session the next month is set
// at, in 1 write, all or nothing
const closeArgs = ({ person, product, outcomes, bodies, session, openNext = true }) => ({
  person, product, outcomes, new: bodies?.length ? bodies : undefined, session: session || null, open_next: openNext,
})
export const closeMonth = (api, payload, ctx) => call(api, 'close_month', closeArgs(payload), ctx)
// The same close as the page goes (closed, reloaded, or hidden on a phone, which may never come back to it): a fetch with
// keepalive, which the browser finishes after the page has gone, with the session's token read when the close was held
// (supabase-js reads the session again before it sends, too late as a page unloads). Its answer is read as closeMonth's
// is, while the page is still there to read it.
export async function closeMonthAsPageGoes(api, payload, ctx, token) {
  let res
  try {
    res = await fetch(`${ENV.supabaseUrl}/rest/v1/rpc/close_month`, {
      method: 'POST',
      keepalive: true,
      headers: {
        apikey: ENV.publishableKey, authorization: `Bearer ${token}`, 'content-type': 'application/json', 'content-profile': 'hub', accept: 'application/json',
      },
      body: JSON.stringify(args(closeArgs(payload))),
    })
  } catch (e) {
    throw refused(api, e, ctx)
  }
  const data = await res.json().catch(() => null)
  if (!res.ok) throw refused(api, data && typeof data === 'object' ? data : { message: res.statusText }, ctx)
  return data
}
// a closed month's action, corrected with a reason
export const correctOutcome = (api, { action, outcome, reason }, ctx) => call(api, 'correct_outcome', { action, outcome, reason }, ctx)
// an absence caught up on, or cleared
export const recordCatchUp = (api, { session, person, on }, ctx) => call(api, 'record_catch_up', { session, person, on: on || null }, ctx)
// What on track means (an owner only)
export const setRules = (api, { product, rules }, ctx) => call(api, 'set_accountability_rules', { product, rules }, ctx)
// a member's level set by hand with a note, or back to automatic (level null): migration 80
export const setLevel = (api, { person, product, level, note }, ctx) =>
  call(api, 'set_accountability_level', { person, product, level: level || null, note: note?.trim() ? note.trim() : null }, ctx)
// the session the open month is set at, or none: migration 80
export const setMonthSession = (api, { person, product, session }, ctx) =>
  call(api, 'set_month_session', { person, product, session: session || null }, { ...ctx, monthSession: true })
// A copy of the log kept as it's saved as a PDF
export const noteSaved = (api, { person, product }, ctx) => call(api, 'note_log_saved', { person, product }, ctx)
// The email with a link to the member's log (send-log-link): its done line, or a refusal in its own words
export const sendLink = (api, { person, product, line }) => api.send_log_link({ person_id: person, product_id: product, line: line?.trim() ? line.trim() : undefined })
// A register mark from the member view: the same write the register makes (now audited)
export const setMark = (api, { session, person, status }) => api.register_line({ session_id: session, person_id: person, status })
// A member's group, from the member view: the access row's audited change, as on the person page
export const moveGroup = (api, { accessId, cohortId }) => api.update_access(accessId, { cohort_id: cohortId || null })

// ---- Wins (owner and admin write any; a member changes and deletes the wins they logged, and on 1 staff
// logged only its share tick: migration 20261007120000, Adam's follow-up of 7 Oct) ----------------------

export async function addWin(api, { person, product, title, category, winDate, detail, shareable }, ctx) {
  const row = { person_id: person, product_id: product, title: title.trim(), category, shareable: Boolean(shareable) }
  if (winDate) row.win_date = winDate
  if (detail?.trim()) row.detail = detail.trim()
  return (await write(api, api.from('wins').insert(row).select(MM_COLUMNS.wins), ctx))[0]
}
// shareable goes only when it's given: the panel never sends it for a saved win, whoever logged it (only the
// member says yes to sharing a win of theirs: 42501 share_member_only, migration 20261007110000)
export async function saveWin(api, { id, title, category, winDate, detail, shareable }, ctx) {
  const patch = { title: title.trim(), category, win_date: winDate, detail: detail?.trim() ? detail.trim() : null }
  if (shareable !== undefined) patch.shareable = Boolean(shareable)
  const rows = await write(api, api.from('wins').update(patch).eq('id', id).select(MM_COLUMNS.wins), ctx)
  if (!rows?.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
  return rows[0]
}
export async function deleteWin(api, id, ctx) {
  const rows = await write(api, api.from('wins').delete().eq('id', id).select('id'), ctx)
  if (!rows?.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
  return rows[0]
}

// ---- Groups and the sessions for every group (owner, admin; Recordings and dates) ---------------------

const groupWords = name => err => {
  if (err?.code === '23505') return GROUPS.nameTaken(name)
  if (err?.code === '23514') return GROUPS.nameBad
  if (err?.code === '23503') return GROUPS.removeRefused
  return null
}

// A new group on a mastermind: named by its naming ("Group 3"), with the last group's schedule line
export async function addGroup(api, { product, cohorts, name, number, personId }) {
  const mine = cohorts.filter(c => c.product_id === product.id)
  const last = mine.slice().sort((a, b) => (b.sort_order ?? 0) - (a.sort_order ?? 0))[0]
  const row = {
    product_id: product.id, name, kind: 'group', number, schedule_label: last?.schedule_label || null,
    sort_order: Math.max(0, ...mine.map(c => c.sort_order ?? 0)) + 1, created_by: personId,
  }
  return (await write(api, api.from('cohorts').insert(row).select(MM_COLUMNS.cohorts), {}, groupWords(name)))[0]
}
export async function renameGroup(api, { cohortId, name }) {
  const clean = String(name || '').trim().replace(/\s+/g, ' ')
  if (!clean || clean.length > 80) throw new api.AdminError('23514', 400, GROUPS.nameBad)
  const rows = await write(api, api.from('cohorts').update({ name: clean }).eq('id', cohortId).select(MM_COLUMNS.cohorts), {}, groupWords(clean))
  if (!rows?.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
  return rows[0]
}
// Only a group with nobody in it, no sessions, recordings or prices: the database refuses the rest
// (23503), and its WhatsApp link goes with it (cohort_links cascades)
export async function removeGroup(api, { cohortId, name }) {
  const rows = await write(api, api.from('cohorts').delete().eq('id', cohortId).select('id'), {}, groupWords(name))
  if (!rows?.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
  return rows[0]
}
// A session for every group (cohort_id null), with no date yet
export async function addEverySession(api, { product, sessions, personId }) {
  const last = sessions.filter(s => s.product_id === product.id).sort((a, b) => Date.parse(b.starts_at || 0) - Date.parse(a.starts_at || 0))[0]
  const row = {
    product_id: product.id, cohort_id: null, title: GROUPS.everyTitle,
    duration_minutes: last?.duration_minutes || product.default_session_minutes || 90, status: 'scheduled', updated_by: personId,
  }
  return (await write(api, api.from('sessions').insert(row).select(MM_COLUMNS.every), {}))[0]
}

// ---- Registers: Mark all present (owner, admin, support; a host for their product's) ------------------

// Everyone expected who isn't marked yet, marked present in 1 write; anyone already marked keeps it
export async function markAllPresent(api, { sessionId, rows }) {
  const lines = rows.filter(r => !r.status).map(r => ({ session_id: sessionId, person_id: r.person_id, status: 'present' }))
  if (!lines.length) return []
  return write(api, api.from('attendance').upsert(lines, { onConflict: 'session_id,person_id' }).select(MM_COLUMNS.attendance), {})
}
